import { ArticleType, ArticleTypeBasic } from "config/constants"
import { AssetOwnershipType, AssetStatus } from "models/Asset"
import { LmidModalMode } from "components/LmidModal/lmidModalMode"

export { LmidModalMode }

/**
 * The source characteristics that decide which LMID modes a copy may use:
 * whether the source is a machine or equipment, and whether it was manufactured
 * by Liebherr or an external manufacturer.
 */
export enum CopyAssetSourceKind {
    machineLh = "machineLh",
    machineNonLh = "machineNonLh",
    equipmentLh = "equipmentLh",
    equipmentNonLh = "equipmentNonLh",
}

interface IAllowedCombination {
    sourceKind: CopyAssetSourceKind
    ownershipType: AssetOwnershipType
    status: AssetStatus
    lmidMode: LmidModalMode
}

const TARGET_STATUSES_BY_OWNERSHIP: Record<AssetOwnershipType, AssetStatus[]> = {
    [AssetOwnershipType.purchased]: [AssetStatus.ordered, AssetStatus.confirmed, AssetStatus.available],
    [AssetOwnershipType.borrowed]: [AssetStatus.ordered, AssetStatus.confirmed, AssetStatus.active],
}

const ORDERED_OR_CONFIRMED: AssetStatus[] = [AssetStatus.ordered, AssetStatus.confirmed]

/**
 * Builds the allowed `(source kind, ownership, target status, LMID mode)`
 * combinations once, so that the LMID options and the status options are always
 * derived from the very same table and can never contradict each other.
 *
 * Rules encoded here:
 * - "Enter LMID" is available in every combination and is the default.
 * - "Enter LMID later" is only available for purchased copies that are ordered or
 *   confirmed — an asset that is already available cannot be saved without an LMID.
 * - "Request LMID" is unavailable for Liebherr machines, which are produced with
 *   an LMID already assigned, and available for everything else.
 * - A purchased copy is never `active`, a borrowed copy is never `available`.
 */
function buildAllowedCombinations(): IAllowedCombination[] {
    const combinations: IAllowedCombination[] = []

    for (const sourceKind of Object.values(CopyAssetSourceKind)) {
        const canRequestLmid = sourceKind !== CopyAssetSourceKind.machineLh

        for (const ownershipType of Object.values(AssetOwnershipType)) {
            for (const status of TARGET_STATUSES_BY_OWNERSHIP[ownershipType]) {
                const lmidModes = [LmidModalMode.enterLmid]

                if (ownershipType === AssetOwnershipType.purchased && ORDERED_OR_CONFIRMED.includes(status)) {
                    lmidModes.push(LmidModalMode.enterLmidLater)
                }

                if (canRequestLmid) {
                    lmidModes.push(LmidModalMode.requestLmid)
                }

                for (const lmidMode of lmidModes) {
                    combinations.push({ sourceKind, ownershipType, status, lmidMode })
                }
            }
        }
    }

    return combinations
}

const ALLOWED_COMBINATIONS = buildAllowedCombinations()

/** Derives the source kind from the source asset's article. */
export function getCopyAssetSourceKind(articleType: ArticleType, isExtManufacturer: boolean): CopyAssetSourceKind {
    const isMachine = articleType === ArticleType.machine
    if (isMachine) {
        return isExtManufacturer ? CopyAssetSourceKind.machineNonLh : CopyAssetSourceKind.machineLh
    }
    return isExtManufacturer ? CopyAssetSourceKind.equipmentNonLh : CopyAssetSourceKind.equipmentLh
}

/** Maps an article type to the basic machine/equipment distinction used by the LMID modal. */
export function toArticleTypeBasic(articleType: ArticleType): ArticleTypeBasic {
    return articleType === ArticleType.machine ? ArticleTypeBasic.machine : ArticleTypeBasic.equipment
}

/** LMID modes selectable for a copy with the given source, ownership and target status. */
export function allowedLmidModes(
    sourceKind: CopyAssetSourceKind,
    ownershipType: AssetOwnershipType,
    status: AssetStatus,
): LmidModalMode[] {
    return ALLOWED_COMBINATIONS
        .filter(c => c.sourceKind === sourceKind && c.ownershipType === ownershipType && c.status === status)
        .map(c => c.lmidMode)
}

/** Target statuses selectable for a copy with the given source, ownership and LMID mode. */
export function allowedStatuses(
    sourceKind: CopyAssetSourceKind,
    ownershipType: AssetOwnershipType,
    lmidMode: LmidModalMode,
): AssetStatus[] {
    return TARGET_STATUSES_BY_OWNERSHIP[ownershipType]
        .filter(status => allowedLmidModes(sourceKind, ownershipType, status).includes(lmidMode))
}

/** All target statuses offered for an ownership, regardless of the LMID mode. */
export function targetStatuses(ownershipType: AssetOwnershipType): AssetStatus[] {
    return TARGET_STATUSES_BY_OWNERSHIP[ownershipType]
}

/**
 * Pre-selected target status of a copy.
 *
 * An ordered, confirmed or available source keeps its own status when the target
 * ownership still offers it. Any other source state — active, inactive or
 * disposed — starts the copy at the first lifecycle state that makes sense for
 * the chosen ownership: `available` when purchased, `active` when borrowed.
 */
export function statusPrefill(sourceStatus: AssetStatus, ownershipType: AssetOwnershipType): AssetStatus {
    const offered = TARGET_STATUSES_BY_OWNERSHIP[ownershipType]
    if (offered.includes(sourceStatus)) return sourceStatus
    return ownershipType === AssetOwnershipType.purchased ? AssetStatus.available : AssetStatus.active
}

/**
 * Pre-selected LMID mode of a copy: "Enter LMID" whenever it is allowed, which
 * per the matrix is always the case.
 */
export function lmidModePrefill(
    sourceKind: CopyAssetSourceKind,
    ownershipType: AssetOwnershipType,
    status: AssetStatus,
): LmidModalMode {
    const modes = allowedLmidModes(sourceKind, ownershipType, status)
    return modes.includes(LmidModalMode.enterLmid) ? LmidModalMode.enterLmid : modes[0]
}
